import * as THREE from "three";

/** Les trois morceaux d'une carte a coins arrondis : face, dos et tranche. */
export interface CardGeometrySet {
  front: THREE.BufferGeometry;
  back: THREE.BufferGeometry;
  side: THREE.BufferGeometry;
}

/**
 * Carte a vrais coins arrondis (contour en arcs, comme une carte a jouer
 * reelle) : trois geometries separees plutot qu'un seul BoxGeometry, pour
 * garder trois materiaux distincts (face/dos/tranche) sans se heurter aux
 * groupes fusionnes d'ExtrudeGeometry (qui melange face et dos). Le contour
 * arrondi etant convexe, la triangulation en eventail suffit.
 */
export function buildCardGeometry(
  width: number,
  height: number,
  depth: number,
  radius: number,
  cornerSegments = 6,
): CardGeometrySet {
  const hw = width / 2;
  const hh = height / 2;
  const r = Math.min(radius, hw, hh);

  // Contour trigonometrique (CCW vu depuis +Z) : quatre arcs de coin relies
  // par les bords droits (implicites : le point final d'un arc = debut du suivant).
  const corners: Array<[number, number, number]> = [
    [hw - r, hh - r, 0],
    [-(hw - r), hh - r, Math.PI / 2],
    [-(hw - r), -(hh - r), Math.PI],
    [hw - r, -(hh - r), -Math.PI / 2],
  ];
  const points: THREE.Vector2[] = [];
  for (const [cx, cy, startAngle] of corners) {
    for (let i = 0; i <= cornerSegments; i++) {
      const a = startAngle + (i / cornerSegments) * (Math.PI / 2);
      points.push(new THREE.Vector2(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
    }
  }

  return {
    front: capGeometry(points, width, height, depth / 2, false),
    back: capGeometry(points, width, height, -depth / 2, true),
    side: sideGeometry(points, depth),
  };
}

/** Face plane (eventail depuis le point 0), UV normalisees sur la boite englobante. */
function capGeometry(
  points: THREE.Vector2[],
  width: number,
  height: number,
  z: number,
  flip: boolean,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const first = points[0]!;
  const pushVertex = (p: THREE.Vector2) => {
    positions.push(p.x, p.y, z);
    uvs.push((p.x + width / 2) / width, (p.y + height / 2) / height);
  };
  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (!flip) {
      pushVertex(first);
      pushVertex(a);
      pushVertex(b);
    } else {
      pushVertex(first);
      pushVertex(b);
      pushVertex(a);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geo.computeVertexNormals();
  return geo;
}

/** Tranche : ruban de quads le long du contour, normale constante par segment (pas de texture, l'UV importe peu). */
function sideGeometry(points: THREE.Vector2[], depth: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const hz = depth / 2;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    const edge = new THREE.Vector2(b.x - a.x, b.y - a.y);
    if (edge.lengthSq() < 1e-10) continue;
    edge.normalize();
    const normal = new THREE.Vector2(edge.y, -edge.x);
    const a0 = [a.x, a.y, -hz];
    const a1 = [a.x, a.y, hz];
    const b0 = [b.x, b.y, -hz];
    const b1 = [b.x, b.y, hz];
    positions.push(...a0, ...b0, ...b1, ...a0, ...b1, ...a1);
    for (let k = 0; k < 6; k++) normals.push(normal.x, normal.y, 0);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  return geo;
}
